import { PauseIcon, PlayIcon, RotateCcwIcon } from "lucide-react";

import { DATA_LABEL_CLS } from "@/components/editorial";

const INK_72 = "text-[color-mix(in_srgb,var(--theme-ink)_72%,transparent)]";

export type ReplayState = "idle" | "playing" | "paused" | "ended";

const LABELS: Record<ReplayState, { text: string; aria: string }> = {
  idle: { text: "Play", aria: "Play the sample session" },
  playing: { text: "Pause", aria: "Pause the sample session" },
  paused: { text: "Play", aria: "Resume the sample session" },
  ended: { text: "Replay", aria: "Replay the sample session" },
};

/**
 * The replay's control row beneath the frame: one button that pauses,
 * resumes or replays, and a hairline showing how far the session has run.
 * Rendered by the server before the player arrives (disabled, same height),
 * so the row never shifts the page.
 */
export function ReplayControls({
  state,
  progress,
  onToggle,
}: {
  state: ReplayState;
  progress: number;
  onToggle?: () => void;
}) {
  const label = LABELS[state];
  const Icon = state === "playing" ? PauseIcon : state === "ended" ? RotateCcwIcon : PlayIcon;
  return (
    <div className="mt-3 flex h-9 items-center gap-4">
      <button
        type="button"
        onClick={onToggle}
        disabled={!onToggle}
        aria-label={label.aria}
        className={`${DATA_LABEL_CLS} ${INK_72} inline-flex h-9 items-center gap-2 border border-[var(--rule)] px-3 transition-colors hover:text-[var(--theme-accent-text)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--theme-accent-text)] disabled:opacity-60`}
      >
        <Icon aria-hidden="true" className="size-3.5" />
        {label.text}
      </button>
      <div aria-hidden="true" className="relative h-px flex-1 bg-[var(--rule)]">
        <div
          className="absolute inset-y-0 left-0 bg-[color-mix(in_srgb,var(--theme-ink)_58%,transparent)]"
          style={{ width: `${Math.round(Math.min(1, Math.max(0, progress)) * 1000) / 10}%` }}
        />
      </div>
    </div>
  );
}
